import { useTranslation } from 'react-i18next';
import { FormSection, Formblock, Select } from '@zolteam/zol-admin-ui';
import { FormElements } from '@zolteam/zol-react-form';
import AutocompleteCustomers from '../../../organisms/autocompleteCustomers/AutocompleteCustomers';
import { BOOKING_STATUS, isStatusChangeTriggeringANotification } from '../../../../domain/bookings/bookingsUseCases';

/**
 * @typedef {object} BookingGeneralSectionProps
 * @property {import('@zolteam/zol-react-form/types').ConfigObject} formConfig
 * @property {{formName: string}} formInfo - The form information object
 * @property {string} status - Current booking status
 * @property {{value: string, label: string} | null} customer - Selected customer
 * @property {string} customerError - Customer field error message
 * @property {(status: string, formInfo: object) => void} handleSetStatus - Status change handler
 * @property {(customer: {value: string, label: string},
 *  formInfo: any) => void} handleSetCustomer - Customer change handler
 * @property {(formInfo: object) => void} handleCustomerBlur - Customer field blur handler
 */

/**
 * General section of the booking form
 * @param {BookingGeneralSectionProps} props
 * @returns {JSX.Element}
 */
function BookingGeneralSection({
  formConfig,
  formInfo,
  status,
  customer,
  customerError,
  handleSetStatus,
  handleSetCustomer,
  handleCustomerBlur,
}) {
  const { t } = useTranslation();

  return (
    <FormSection label={t('bookingForm.section.general')} className="margin-top-m">
      <div className="d-flex f-row gap-m f-wrap align-end">
        <Formblock
          key="status"
          htmlFor="status"
          label={t('bookingList.filters.status')}
          className="margin-bottom-xs"
        >
          <Select
            // @ts-ignore
            id="status"
            name="status"
            className="background-neutral00"
            placeholder={t('bookingList.filters.placeholders.status')}
            options={BOOKING_STATUS}
            value={status || ''}
            onChange={
              (
                /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
              ) => handleSetStatus(e?.target?.value, formInfo)
              }
          />
        </Formblock>
        <div className="margin-bottom-xs">
          <AutocompleteCustomers
            label={t('bookingForm.fields.customer')}
            value={customer}
            setCustomer={(
              /** @type {{label: string, value: string}} */newCustomer,
            ) => handleSetCustomer(newCustomer, formInfo)}
            errorMessage={customerError}
            name="customerId"
            onBlur={() => handleCustomerBlur(formInfo)}
          />
        </div>
      </div>
      {status && isStatusChangeTriggeringANotification(status) && (
      <div className="margin-top-xxs font-color-primary-500">
        {t('bookingForm.warnings.statusNotification')}
      </div>
      )}
      {status === 'canceled' && (
        <div className="margin-top-s">
          <FormElements
            translations={null}
            formConfig={{ cancellationReason: formConfig.cancellationReason }}
            formTitle={formInfo.formName}
          />
        </div>
      )}
    </FormSection>
  );
}

export default BookingGeneralSection;
